import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { useAuth, tokenSentWith } from '../../context/AuthContext';
import PlatformLayout from '../../components/layout/PlatformLayout';
import { PUBLIC_WEB_URL } from '../../config/api';
import './Platform.css';
import AddPeopleModal from './AddPeopleModal';
import TestPasswordModal from './TestPasswordModal';
import SetPasswordModal from './SetPasswordModal';
import { isSuperAdminOrOwner } from '../../lib/staffRoles';
import {
  Building2,
  Plus,
  Mail,
  ChevronDown,
  X,
  AlertCircle,
  Edit,
  Lock,
  Unlock,
  Search,
  Users,
  Eye,
  Loader,
  Copy,
  CheckCircle2,
  ShieldCheck,
  UserPlus,
  UserMinus,
  EyeOff,
  KeyRound,
  FlaskConical,
  Send
} from 'lucide-react';

const COLOR_DEFAULTS = { primary_color: '#1E4A35', secondary_color: '#2C2B28', tertiary_color: '#F4EFE6', accent_color: '#C4943A' };
const COLOR_FIELDS = [['primary_color', 'Primary'], ['secondary_color', 'Secondary'], ['tertiary_color', 'Tertiary'], ['accent_color', 'Accent']];
// How many colours a club has actually customised (trailing defaults don't count). Min 1.
const coloursUsed = (src) => {
  let n = 1;
  COLOR_FIELDS.forEach(([key], i) => {
    if (i > 0 && (src?.[key] || COLOR_DEFAULTS[key]).toLowerCase() !== COLOR_DEFAULTS[key].toLowerCase()) n = i + 1;
  });
  return n;
};

export default function PlatformClubs() {
  const { platformAxios, platformUser } = usePlatformAuth();
  const isSuperAdmin = isSuperAdminOrOwner(platformUser?.role); // v51: only super admins (and the Owner, v53) flip Test club
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [expandedClub, setExpandedClub] = useState(null);
  const [expandedMembers, setExpandedMembers] = useState(null); // club id whose member list is open

  // Deep link from the dashboard (SAP clickthrough, 24 Aug): /clubs?club=<id>
  // opens with that club's row expanded and scrolled into view.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get('club');
    if (!target) return;
    setExpandedClub(target);
    setSearchParams({}, { replace: true }); // one-shot — don't re-expand on later renders
    setTimeout(() => {
      document.querySelector('.expanded-row')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    // eslint-disable-next-line
  }, []);
  const [formError, setFormError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [impersonatingUserId, setImpersonatingUserId] = useState(null); // track loading state
  const [addPeopleClub, setAddPeopleClub] = useState(null); // club whose Add people modal is open
  const [confirmRemoveId, setConfirmRemoveId] = useState(null); // member_id awaiting a second tap
  const [removeError, setRemoveError] = useState('');
  const [testPwMember, setTestPwMember] = useState(null); // member whose Set test password modal is open
  const [setPwMember, setSetPwMember] = useState(null);   // v53: member whose Owner Set password modal is open
  const [resetNote, setResetNote] = useState(null);       // { userId, ok, text } after Send reset link
  const [createColorCount, setCreateColorCount] = useState(1); // colours visible in create modal (start with 1)
  const [editColorCount, setEditColorCount] = useState(1);

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '', slug: '', sport: '', location: '', region: '', county: '', postcode: '', description: '',
    nickname: '', founded_year: '', phone: '', website: '',
    primary_color: '#1E4A35', secondary_color: '#2C2B28', tertiary_color: '#F4EFE6', accent_color: '#C4943A'
  });
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'admin' });
  // F1-3: after a successful invite, hold the server-built link so the admin can copy it
  const [inviteResult, setInviteResult] = useState(null); // { email, inviteLink }
  const [copiedInviteId, setCopiedInviteId] = useState(null); // 'modal' or an invitation id
  const [editForm, setEditForm] = useState({ name: '', sport: '', location: '', description: '', website: '', nickname: '', founded_year: '', phone: '',
    primary_color: '#1E4A35', secondary_color: '#2C2B28', tertiary_color: '#F4EFE6', accent_color: '#C4943A' });

  const { data: clubs, isLoading } = useQuery({
    queryKey: ['platform-clubs'],
    queryFn: () => platformAxios.get('/clubs').then(res => res.data)
  });

  // F1-3: pending club-admin invitations (shown per club in the expanded row, with copy)
  const { data: invitations } = useQuery({
    queryKey: ['platform-invitations'],
    queryFn: () => platformAxios.get('/invitations').then(res => res.data)
  });

  // Fetch members for expanded club (only when members panel is open)
  const { data: clubMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['platform-club-members', expandedMembers],
    queryFn: () => platformAxios.get(`/clubs/${expandedMembers}/members`).then(res => res.data),
    enabled: !!expandedMembers
  });

  // Remove someone from a club (same rules as Club Admin — last admin protected)
  const removeMemberMutation = useMutation({
    mutationFn: ({ clubId, memberId }) => platformAxios.delete(`/clubs/${clubId}/members/${memberId}`),
    onSuccess: (_res, { clubId }) => {
      setConfirmRemoveId(null);
      queryClient.invalidateQueries({ queryKey: ['platform-club-members', clubId] });
      queryClient.invalidateQueries({ queryKey: ['platform-clubs'] });
    },
    onError: (err) => {
      setConfirmRemoveId(null);
      setRemoveError(err.response?.data?.error || 'Could not remove them');
    },
  });

  // SAP password tools (28 Oct): email the normal forgot-password link.
  const sendResetMutation = useMutation({
    mutationFn: (member) => platformAxios.post(`/users/${member.user_id}/send-reset`),
    onSuccess: (_res, member) => setResetNote({ userId: member.user_id, ok: true, text: 'Reset email sent.' }),
    onError: (err, member) => setResetNote({ userId: member.user_id, ok: false, text: err.response?.data?.error || 'Could not send the reset email' }),
  });
  const handleSendReset = (member) => {
    const who = [member.first_name, member.last_name ? `${member.last_name.charAt(0)}.` : ''].filter(Boolean).join(' ') || member.email;
    if (!window.confirm(`Send a password reset email to ${who}? They choose their own password.`)) return;
    setResetNote(null);
    sendResetMutation.mutate(member);
  };

  // Filtered clubs
  const filteredClubs = useMemo(() => {
    if (!clubs) return [];
    return clubs.filter(club => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q ||
        club.name.toLowerCase().includes(q) ||
        (club.location || '').toLowerCase().includes(q) ||
        (club.slug || '').toLowerCase().includes(q);
      const matchesSport = !sportFilter || (club.sport || '').toLowerCase().includes(sportFilter.toLowerCase());
      const matchesStatus = !statusFilter || club.status === statusFilter;
      return matchesSearch && matchesSport && matchesStatus;
    });
  }, [clubs, searchQuery, sportFilter, statusFilter]);

  // Unique sports for filter dropdown
  const sportOptions = useMemo(() => {
    if (!clubs) return [];
    const sports = [...new Set(clubs.map(c => c.sport).filter(Boolean))].sort();
    return sports;
  }, [clubs]);

  const createClubMutation = useMutation({
    mutationFn: (data) => platformAxios.post('/clubs', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-clubs'] });
      setShowCreateModal(false);
      setCreateForm({ name: '', slug: '', sport: '', location: '', region: '', county: '', postcode: '', description: '',
        nickname: '', founded_year: '', phone: '', website: '',
        primary_color: '#1E4A35', secondary_color: '#2C2B28', tertiary_color: '#F4EFE6', accent_color: '#C4943A' });
      setCreateColorCount(1);
      setFormError('');
    },
    onError: (err) => setFormError(err.response?.data?.error || 'Failed to create club')
  });

  const inviteMutation = useMutation({
    mutationFn: (data) => platformAxios.post(`/clubs/${selectedClubId}/invite`, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['platform-clubs'] });
      queryClient.invalidateQueries({ queryKey: ['platform-invitations'] });
      // F1-3: keep the modal open and show the server-built invite link with a Copy button
      setInviteResult({ email: res.data.email, inviteLink: res.data.inviteLink });
      setInviteForm({ email: '', role: 'admin' });
      setFormError('');
    },
    onError: (err) => setFormError(err.response?.data?.error || 'Failed to send invitation')
  });

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteResult(null);
    setFormError('');
  };

  // B-38 follow-up: Resend from the per-club invite list. An expired invite
  // gets a fresh 7 days (server rule, shared with Club Admin); refetch shows it.
  const [resentInviteId, setResentInviteId] = useState(null);
  const [resendError, setResendError] = useState('');
  const resendInviteMutation = useMutation({
    mutationFn: (inviteId) => platformAxios.post(`/invitations/${inviteId}/resend`),
    onSuccess: (_res, inviteId) => {
      setResendError('');
      queryClient.invalidateQueries({ queryKey: ['platform-invitations'] });
      setResentInviteId(inviteId);
      setTimeout(() => setResentInviteId(null), 3000);
    },
    onError: (err) => setResendError(err.response?.data?.error || 'Could not resend the invite'),
  });

  const copyInviteLink = (link, id) => {
    navigator.clipboard.writeText(link);
    setCopiedInviteId(id);
    setTimeout(() => setCopiedInviteId(null), 2000);
  };

  const editClubMutation = useMutation({
    mutationFn: (data) => platformAxios.patch(`/clubs/${selectedClubId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-clubs'] });
      setShowEditModal(false);
      setFormError('');
    },
    onError: (err) => setFormError(err.response?.data?.error || 'Failed to update club')
  });

  const updateClubStatusMutation = useMutation({
    mutationFn: ({ clubId, status }) => platformAxios.patch(`/clubs/${clubId}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-clubs'] }),
    onError: (err) => alert(err.response?.data?.error || 'Failed to update status')
  });

  // v33 — verify a self-serve club: unlocks its sponsorship/QR tools.
  // One-way; suspension remains the lever for problem clubs.
  const verifyClubMutation = useMutation({
    mutationFn: (clubId) => platformAxios.patch(`/clubs/${clubId}/verify`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-clubs'] }),
    onError: (err) => alert(err.response?.data?.error || 'Failed to verify club')
  });

  const handleVerify = (club) => {
    if (window.confirm(`Verify ${club.name}? This unlocks their sponsorship & QR tools — it can't be undone (use Suspend for problem clubs).`)) {
      verifyClubMutation.mutate(club.id);
    }
  };

  // v49 — staff "hide from search" switch for test / demo clubs: keeps the
  // club out of Find your club and Fixture Finder. Members and links still work.
  // v51 — staff-only "Test club" switch (lets staff set test passwords).
  const testClubMutation = useMutation({
    mutationFn: ({ clubId, isTest }) => platformAxios.patch(`/clubs/${clubId}/test-club`, { isTest }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-clubs'] });
      queryClient.invalidateQueries({ queryKey: ['platform-club-members'] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to update the test club setting')
  });

  const searchVisibilityMutation = useMutation({
    mutationFn: ({ clubId, hidden }) => platformAxios.patch(`/clubs/${clubId}/search-visibility`, { hidden }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-clubs'] }),
    onError: (err) => alert(err.response?.data?.error || 'Failed to update search visibility')
  });

  const handleCreateSubmit = (e) => { e.preventDefault(); setFormError(''); createClubMutation.mutate(createForm); };
  const handleInviteSubmit = (e) => { e.preventDefault(); setFormError(''); inviteMutation.mutate(inviteForm); };
  const handleEditSubmit = (e) => { e.preventDefault(); setFormError(''); editClubMutation.mutate(editForm); };

  const handleEditClick = (club) => {
    setSelectedClubId(club.id);
    setEditForm({ name: club.name, sport: club.sport || '', location: club.location || '', description: club.description || '', website: club.website || '',
      nickname: club.nickname || '', founded_year: club.founded_year || '', phone: club.phone || '',
      primary_color: club.primary_color || '#1E4A35', secondary_color: club.secondary_color || '#2C2B28',
      tertiary_color: club.tertiary_color || '#F4EFE6', accent_color: club.accent_color || '#C4943A' });
    setEditColorCount(coloursUsed(club));
    setShowEditModal(true);
  };

  // B-38 item 4 (20 Oct): suspending now really locks the club (members and
  // its own admins see "This club is paused"; staff keep access), so say so.
  const handleStatusToggle = (club) => {
    const newStatus = club.status === 'suspended' ? 'active' : 'suspended';
    const question = newStatus === 'suspended'
      ? `Suspend ${club.name}? Members and admins will be locked out until you reactivate it. Nothing is deleted.`
      : `Reactivate ${club.name}? Members and admins get everything back as it was.`;
    if (!window.confirm(question)) return;
    updateClubStatusMutation.mutate({ clubId: club.id, status: newStatus });
  };

  const handleImpersonate = async (member, club) => {
    setImpersonatingUserId(member.user_id);
    try {
      const res = await platformAxios.post(`/impersonate/${member.user_id}`);
      // B-71: the staffer's own token (this request was sent with it), in case
      // this is a second "view as" with nothing stashed to go back to.
      await impersonate(res.data.token, res.data.user, { staffToken: tokenSentWith(res.config) });
      // Store the current admin page so Exit can return here
      localStorage.setItem('marakin_impersonation_return_url', window.location.pathname);
      // Navigate to the club picker — the user may belong to multiple clubs
      navigate('/my-clubs');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to impersonate user');
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');

  const handleNameChange = (e) => {
    const name = e.target.value;
    setCreateForm(prev => ({ ...prev, name, slug: generateSlug(name) }));
  };

  const toggleMembersPanel = (e, clubId) => {
    e.stopPropagation();
    setExpandedMembers(expandedMembers === clubId ? null : clubId);
  };

  const roleLabel = (role) => {
    const labels = { admin: 'Club Admin', team_manager: 'Team Mgr', coach: 'Coach', player: 'Player', parent: 'Parent / Guardian', supporter: 'Supporter', sponsor: 'Sponsor' };
    return labels[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1) : role);
  };

  // Colour picker: start with Primary, "+" adds the next colour up to 4, "×" removes the last-added.
  // Hidden colours keep Marakin defaults, so the backend always receives 4 valid values.
  const renderColourPicker = (form, setForm, count, setCount) => (
    <div className="form-group">
      <label>Club colours</label>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {COLOR_FIELDS.slice(0, count).map(([key, lbl], i) => (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{lbl}</span>
            <input type="color" value={form[key]}
              onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
              style={{ width: 44, height: 38, padding: 2, borderRadius: 6, cursor: 'pointer' }} />
            {i === count - 1 && i > 0 && (
              <button type="button" aria-label={`Remove ${lbl} colour`}
                onClick={() => { setForm(prev => ({ ...prev, [key]: COLOR_DEFAULTS[key] })); setCount(count - 1); }}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: '#999', color: '#fff', fontSize: 12, lineHeight: '18px', cursor: 'pointer', padding: 0 }}>×</button>
            )}
          </div>
        ))}
        {count < 4 && (
          <button type="button" onClick={() => setCount(count + 1)} aria-label="Add another colour"
            style={{ width: 44, height: 38, borderRadius: 6, border: '1px dashed #bbb', background: 'transparent', color: '#666', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
        )}
      </div>
      <p style={{ fontSize: 12, color: '#888', margin: '6px 0 0' }}>
        Start with your main colour. Add up to four — any you don't set use Marakin defaults.
      </p>
    </div>
  );

  return (
    <PlatformLayout>
      <div className="platform-page">
        <div className="page-header">
          <h1>Clubs</h1>
          <p>Manage all clubs on the platform</p>
        </div>

        {/* Toolbar: search/filter + create */}
        <div className="section-toolbar" style={{ flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '240px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#888', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search clubs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', paddingLeft: '32px', paddingRight: '10px', height: '36px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
            </div>
            <select
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value)}
              style={{ height: '36px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.875rem', padding: '0 8px', minWidth: '120px' }}
            >
              <option value="">All sports</option>
              {sportOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ height: '36px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.875rem', padding: '0 8px', minWidth: '110px' }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="onboarding">Onboarding</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} />
            Create Club
          </button>
        </div>

        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading clubs...</p>
          </div>
        ) : filteredClubs.length > 0 ? (
          <div className="clubs-table-container">
            <table className="clubs-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Sport</th>
                  <th>Location</th>
                  <th>Members</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredClubs.map((club) => (
                  <React.Fragment key={club.id}>
                    <tr
                      className="table-row"
                      onClick={() => setExpandedClub(expandedClub === club.id ? null : club.id)}
                    >
                      <td className="club-name-cell">
                        <div className="club-badge"><Building2 size={18} /></div>
                        <div>
                          <strong>{club.name}</strong>
                          <p className="text-muted">{club.slug}</p>
                        </div>
                      </td>
                      <td>{club.sport || '—'}</td>
                      <td>{club.location || '—'}</td>
                      <td className="text-center">{club.member_count}</td>
                      <td>
                        <span className={`status-badge ${club.status}`}>{club.status}</span>
                        {!club.verified_at && (
                          <span className="status-badge unverified" style={{ marginLeft: 6 }}>unverified</span>
                        )}
                        {club.hidden_from_search && (
                          <span className="status-badge search-hidden" style={{ marginLeft: 6 }} title="Hidden from Find your club and Fixture Finder">
                            Hidden
                          </span>
                        )}
                        {!club.verified_at && club.verification_submitted_at && (
                          <span className="status-badge submitted" style={{ marginLeft: 6 }} title={`Submitted ${new Date(club.verification_submitted_at).toLocaleString('en-GB')}`}>
                            submitted {new Date(club.verification_submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </td>
                      <td className="text-muted">{new Date(club.created_at).toLocaleDateString()}</td>
                      <td className="text-center">
                        <ChevronDown
                          size={18}
                          className={`expand-icon ${expandedClub === club.id ? 'expanded' : ''}`}
                        />
                      </td>
                    </tr>

                    {expandedClub === club.id && (
                      <tr className="expanded-row">
                        <td colSpan="7">
                          <div className="expanded-content">
                            <div className="detail-grid">
                              <div className="detail-item">
                                <label>Region</label>
                                <p>{club.region || 'Not set'}</p>
                              </div>
                              <div className="detail-item">
                                <label>County</label>
                                <p>{club.county || 'Not set'}</p>
                              </div>
                              <div className="detail-item">
                                <label>Postcode</label>
                                <p>{club.postcode || 'Not set'}</p>
                              </div>
                            </div>

                            {/* W-2: verification submission — what the admin gave us. */}
                            {!club.verified_at && (
                              <div className="detail-grid" style={{ marginTop: 12, padding: '12px 14px', background: 'rgba(30,74,53,0.06)', borderRadius: 8 }}>
                                <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                                  <label>Verification request</label>
                                  <p>{club.verification_submitted_at
                                    ? `Submitted ${new Date(club.verification_submitted_at).toLocaleString('en-GB')} by ${club.verification_submitted_by_label || 'unknown'} · ${club.verification_role || 'role not given'}`
                                    : 'Not submitted yet — the admin hasn\'t completed their verification details.'}</p>
                                </div>
                                {club.verification_submitted_at && (
                                  <>
                                    <div className="detail-item"><label>Club address</label><p>{[club.home_ground_name, club.home_ground_address].filter(Boolean).join(' — ') || 'Not set'}</p></div>
                                    <div className="detail-item"><label>Website / social</label><p>{club.website ? <a href={/^https?:\/\//i.test(club.website) ? club.website : `https://${club.website}`} target="_blank" rel="noopener noreferrer">{club.website}</a> : 'Not given'}</p></div>
                                    <div className="detail-item"><label>Contact phone</label><p>{club.verification_contact_phone || 'Not given'}</p></div>
                                    <div className="detail-item" style={{ gridColumn: '1 / -1' }}><label>How to check</label><p>{club.verification_notes || 'Not given'}</p></div>
                                  </>
                                )}
                              </div>
                            )}

                            {/* v49 — staff-only: hide test / demo clubs from search. */}
                            <label className="search-visibility-toggle">
                              <input
                                type="checkbox"
                                checked={!!club.hidden_from_search}
                                disabled={searchVisibilityMutation.isPending}
                                onChange={(e) => searchVisibilityMutation.mutate({ clubId: club.id, hidden: e.target.checked })}
                              />
                              <span>
                                <strong><EyeOff size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Hidden from search</strong>
                                <small>Hides this club from Find your club and Fixture Finder. Members and links still work.</small>
                              </span>
                            </label>

                            {/* v51 — super admins only: mark a Marakin test club (the server refuses anyone else). */}
                            {isSuperAdmin ? (
                              <label className="search-visibility-toggle">
                                <input
                                  type="checkbox"
                                  checked={!!club.is_test_club}
                                  disabled={testClubMutation.isPending}
                                  onChange={(e) => testClubMutation.mutate({ clubId: club.id, isTest: e.target.checked })}
                                />
                                <span>
                                  <strong><FlaskConical size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Test club</strong>
                                  <small>Staff can set passwords for made-up test accounts that only belong to test clubs.</small>
                                </span>
                              </label>
                            ) : (
                              <div className="search-visibility-toggle is-readonly">
                                <span>
                                  <strong><FlaskConical size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Test club: {club.is_test_club ? 'yes' : 'no'}</strong>
                                  <small>Only a super admin can change this.</small>
                                </span>
                              </div>
                            )}

                            <div className="expanded-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              {!club.verified_at && (
                                <button
                                  className="btn-primary"
                                  onClick={() => handleVerify(club)}
                                  disabled={verifyClubMutation.isPending}
                                >
                                  <ShieldCheck size={16} /> {verifyClubMutation.isPending ? 'Verifying…' : 'Verify Club'}
                                </button>
                              )}
                              <button className="btn-secondary" onClick={() => handleEditClick(club)}>
                                <Edit size={16} /> Edit Club
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => handleStatusToggle(club)}
                                style={{ opacity: club.status === 'suspended' ? 1 : 0.7, color: club.status === 'suspended' ? '#1E4A35' : '#666' }}
                              >
                                {club.status === 'suspended' ? (
                                  <><Unlock size={16} /> Reactivate</>
                                ) : (
                                  <><Lock size={16} /> Suspend</>
                                )}
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => { setSelectedClubId(club.id); setShowInviteModal(true); }}
                              >
                                <Mail size={16} /> Invite Admin
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => setAddPeopleClub(club)}
                              >
                                <UserPlus size={16} /> Add People
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={(e) => toggleMembersPanel(e, club.id)}
                                style={{ color: expandedMembers === club.id ? '#1E4A35' : undefined }}
                              >
                                <Users size={16} />
                                {expandedMembers === club.id ? 'Hide Members' : 'View Members'}
                              </button>
                            </div>

                            {/* F1-3: Pending club-admin invites with copy-link */}
                            {(() => {
                              // B-38 follow-up: live invites, then ones that expired in the
                              // last 30 days (labelled, with Resend). Older ones stay hidden.
                              const isLive = (inv) => !inv.expires_at || new Date(inv.expires_at) > new Date();
                              const recentCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
                              const ofClub = (invitations || []).filter(inv => inv.slug === club.slug && !inv.accepted_at);
                              const liveInvites = ofClub.filter(isLive);
                              const expiredInvites = ofClub.filter(inv => !isLive(inv) && new Date(inv.expires_at).getTime() > recentCutoff);
                              const clubInvites = [...liveInvites, ...expiredInvites];
                              if (!clubInvites.length) return null;
                              return (
                                <div style={{ marginTop: '16px', borderTop: '1px solid #e8e8e8', paddingTop: '16px' }}>
                                  <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '12px', color: '#2C2B28' }}>
                                    Pending invites ({liveInvites.length}){expiredInvites.length > 0 ? ` · ${expiredInvites.length} expired in the last 30 days` : ''}
                                  </p>
                                  {resendError && <p style={{ color: '#b42318', fontSize: '0.8rem', margin: '0 0 8px' }}>{resendError}</p>}
                                  {clubInvites.map(inv => {
                                    // PUBLIC_WEB_URL, not window.location.origin (F-010)
                                    const invLink = `${PUBLIC_WEB_URL}/${inv.slug}/${inv.kind === 'coparent' ? 'coparent-accept' : 'invite'}/${inv.token}`; // co-parent invites have their own accept page
                                    const expired = !isLive(inv);
                                    return (
                                      <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', fontSize: '0.85rem' }}>
                                        <Mail size={14} style={{ color: '#C4943A', flexShrink: 0 }} />
                                        <span style={{ fontWeight: 600, color: '#2C2B28' }}>{inv.email || [inv.payload?.invitee?.firstName, inv.payload?.invitee?.lastName].filter(Boolean).join(' ') || 'Link-only invite'}</span>
                                        <span style={{ color: '#888' }}>
                                          {roleLabel(inv.role)} · {expired
                                            ? <span style={{ color: '#b42318', fontWeight: 600 }}>Expired {new Date(inv.expires_at).toLocaleDateString()}</span>
                                            : inv.expires_at ? `expires ${new Date(inv.expires_at).toLocaleDateString()}` : 'no expiry date'}
                                        </span>
                                        {!expired && (
                                        <button className="btn-secondary" style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: '0.8rem' }}
                                          onClick={() => copyInviteLink(invLink, inv.id)}>
                                          <Copy size={13} /> {copiedInviteId === inv.id ? 'Copied!' : 'Copy link'}
                                        </button>
                                        )}
                                        {inv.email && (
                                        <button className="btn-secondary" style={{ marginLeft: expired ? 'auto' : 0, padding: '4px 10px', fontSize: '0.8rem' }}
                                          disabled={resendInviteMutation.isPending}
                                          title={expired ? 'Resend the invite email — the link works for 7 more days' : 'Resend the invite email'}
                                          onClick={() => resendInviteMutation.mutate(inv.id)}>
                                          <Mail size={13} /> {resentInviteId === inv.id ? 'Sent!' : 'Resend'}
                                        </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}

                            {/* Members / Impersonation Panel */}
                            {expandedMembers === club.id && (
                              <div style={{ marginTop: '16px', borderTop: '1px solid #e8e8e8', paddingTop: '16px' }}>
                                <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '12px', color: '#2C2B28' }}>
                                  Members — click <Eye size={13} style={{ verticalAlign: 'middle' }} /> to view the app as that person
                                </p>
                                {removeError && (
                                  <div className="error-box" style={{ marginBottom: 12 }}>
                                    <AlertCircle size={16} /><p>{removeError}</p>
                                  </div>
                                )}
                                {membersLoading ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '0.875rem' }}>
                                    <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Loading members...
                                  </div>
                                ) : clubMembers && clubMembers.length > 0 ? (
                                  <div className="member-impersonate-grid">
                                    {clubMembers.map(member => (
                                      <div key={member.member_id} className="member-impersonate-row">
                                        <div>
                                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#2C2B28' }}>
                                            {member.first_name} {member.last_name}
                                          </div>
                                          <div style={{ fontSize: '0.78rem', color: '#888' }}>
                                            {member.email && <div>{member.email}</div>}
                                            <div>
                                              <span style={{ color: '#C4943A', fontWeight: 600 }}>{roleLabel(member.role)}</span>
                                              {member.platform_role && (
                                                <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 100, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', background: '#1E4A35', color: '#F4EFE6' }}>
                                                  ★ Marakin Staff
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="member-actions">
                                        {/* B-69: staff accounts can't be viewed as (the server refuses too) */}
                                        <button
                                          onClick={() => handleImpersonate(member, club)}
                                          disabled={impersonatingUserId === member.user_id || !!member.platform_role}
                                          title={member.platform_role
                                            ? 'Marakin staff accounts can\'t be viewed as'
                                            : `View app as ${member.first_name} ${member.last_name}`}
                                          className="btn-impersonate member-act-view"
                                          style={{ opacity: impersonatingUserId === member.user_id || member.platform_role ? 0.6 : 1 }}
                                        >
                                          {impersonatingUserId === member.user_id
                                            ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
                                            : <Eye size={13} />
                                          }
                                          View as
                                        </button>
                                        {member.password_reset_allowed && (
                                          <button
                                            className="btn-remove-member btn-member-tool member-act-reset"
                                            disabled={sendResetMutation.isPending && sendResetMutation.variables?.user_id === member.user_id}
                                            title={`Email ${member.first_name} a link to choose a new password`}
                                            onClick={() => handleSendReset(member)}
                                          >
                                            <Send size={13} /> Send reset link
                                          </button>
                                        )}
                                        {member.test_password_allowed && (
                                          <button
                                            className="btn-remove-member btn-member-tool member-act-test"
                                            title={`Set a test password for ${member.first_name}`}
                                            onClick={() => { setResetNote(null); setTestPwMember(member); }}
                                          >
                                            <KeyRound size={13} /> Set test password
                                          </button>
                                        )}
                                        {/* v53: the Owner, for everyone else (needs an authenticator code) */}
                                        {!member.test_password_allowed && member.set_password_allowed && (
                                          <button
                                            className="btn-remove-member btn-member-tool member-act-test"
                                            title={`Set a new password for ${member.first_name} (they'll be emailed)`}
                                            onClick={() => { setResetNote(null); setSetPwMember(member); }}
                                          >
                                            <KeyRound size={13} /> Set password
                                          </button>
                                        )}
                                        {resetNote?.userId === member.user_id && (
                                          <span className={`member-tool-note${resetNote.ok ? '' : ' is-error'}`} role="status">
                                            {resetNote.text}
                                          </span>
                                        )}
                                        {confirmRemoveId === member.member_id ? (
                                          <span className="member-remove-confirm member-act-remove">
                                            <button
                                              className="btn-remove-member is-confirm"
                                              disabled={removeMemberMutation.isPending}
                                              onClick={() => { setRemoveError(''); removeMemberMutation.mutate({ clubId: club.id, memberId: member.member_id }); }}
                                            >
                                              {removeMemberMutation.isPending ? 'Removing…' : 'Remove'}
                                            </button>
                                            <button className="btn-remove-member" onClick={() => setConfirmRemoveId(null)}>Keep</button>
                                          </span>
                                        ) : (
                                          <button
                                            className="btn-remove-member member-act-remove"
                                            title={`Remove ${member.first_name} ${member.last_name} from ${club.name}`}
                                            onClick={() => { setRemoveError(''); setConfirmRemoveId(member.member_id); }}
                                          >
                                            <UserMinus size={13} /> Remove
                                          </button>
                                        )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p style={{ color: '#888', fontSize: '0.875rem' }}>No active members found.</p>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {filteredClubs.length < (clubs?.length || 0) && (
              <p style={{ textAlign: 'center', color: '#888', fontSize: '0.875rem', marginTop: '12px' }}>
                Showing {filteredClubs.length} of {clubs.length} clubs
              </p>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>{searchQuery || sportFilter || statusFilter ? 'No clubs match your filters' : 'No clubs yet'}</h3>
            <p>{searchQuery || sportFilter || statusFilter ? 'Try adjusting your search' : 'Create your first club to get started'}</p>
            {!searchQuery && !sportFilter && !statusFilter && (
              <button className="btn-primary" onClick={() => setShowCreateModal(true)}>Create Club</button>
            )}
          </div>
        )}

        {/* Create Club Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create New Club</h2>
                <button className="btn-close" onClick={() => setShowCreateModal(false)}><X size={24} /></button>
              </div>
              <form onSubmit={handleCreateSubmit} className="modal-form">
                {formError && <div className="error-box"><AlertCircle size={18} /><p>{formError}</p></div>}
                <div className="form-group">
                  <label>Club Name *</label>
                  <input type="text" placeholder="e.g., Backwell United RFC" value={createForm.name} onChange={handleNameChange} required />
                </div>
                <div className="form-group">
                  <label>URL Slug *</label>
                  <input type="text" placeholder="e.g., backwell-united-rfc" value={createForm.slug}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, slug: e.target.value }))} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Sport</label>
                    <input type="text" placeholder="e.g., Rugby Union" value={createForm.sport}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, sport: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" placeholder="e.g., Backwell, Somerset" value={createForm.location}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, location: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Region</label>
                    <input type="text" placeholder="e.g., South West" value={createForm.region}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, region: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>County</label>
                    <input type="text" placeholder="e.g., Somerset" value={createForm.county}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, county: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Postcode</label>
                  <input type="text" placeholder="e.g., BS48 1PE" value={createForm.postcode}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, postcode: e.target.value }))} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nickname</label>
                    <input type="text" placeholder="e.g., The Ravens" value={createForm.nickname}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, nickname: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Founded (year)</label>
                    <input type="number" min="1800" max={new Date().getFullYear()} placeholder="e.g., 1893" value={createForm.founded_year}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, founded_year: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Club phone</label>
                    <input type="tel" placeholder="e.g., 01275 123456" value={createForm.phone}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Website</label>
                    <input type="text" placeholder="e.g., www.club.co.uk" value={createForm.website}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, website: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea placeholder="Club description..." value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))} rows={4} />
                </div>
                {renderColourPicker(createForm, setCreateForm, createColorCount, setCreateColorCount)}
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={createClubMutation.isPending}>
                    {createClubMutation.isPending ? 'Creating...' : 'Create Club'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Club Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Edit Club</h2>
                <button className="btn-close" onClick={() => setShowEditModal(false)}><X size={24} /></button>
              </div>
              <form onSubmit={handleEditSubmit} className="modal-form">
                {formError && <div className="error-box"><AlertCircle size={18} /><p>{formError}</p></div>}
                <div className="form-group">
                  <label>Club Name</label>
                  <input type="text" placeholder="e.g., Backwell United RFC" value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Sport</label>
                    <input type="text" placeholder="e.g., Rugby Union" value={editForm.sport}
                      onChange={(e) => setEditForm(prev => ({ ...prev, sport: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" placeholder="e.g., Backwell, Somerset" value={editForm.location}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nickname</label>
                    <input type="text" placeholder="e.g., The Ravens" value={editForm.nickname}
                      onChange={(e) => setEditForm(prev => ({ ...prev, nickname: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Founded (year)</label>
                    <input type="number" min="1800" max={new Date().getFullYear()} placeholder="e.g., 1893" value={editForm.founded_year}
                      onChange={(e) => setEditForm(prev => ({ ...prev, founded_year: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Club phone</label>
                    <input type="tel" placeholder="e.g., 01275 123456" value={editForm.phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Website</label>
                    <input type="url" placeholder="e.g., https://example.com" value={editForm.website}
                      onChange={(e) => setEditForm(prev => ({ ...prev, website: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea placeholder="Club description..." value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} rows={4} />
                </div>
                {renderColourPicker(editForm, setEditForm, editColorCount, setEditColorCount)}
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={editClubMutation.isPending}>
                    {editClubMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {addPeopleClub && (
          <AddPeopleModal club={addPeopleClub} onClose={() => setAddPeopleClub(null)} />
        )}

        {testPwMember && (
          <TestPasswordModal member={testPwMember} onClose={() => setTestPwMember(null)} />
        )}
        {setPwMember && (
          <SetPasswordModal member={setPwMember} onClose={() => setSetPwMember(null)} />
        )}

        {/* Invite Admin Modal */}
        {showInviteModal && (
          <div className="modal-overlay" onClick={closeInviteModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Invite Club Admin</h2>
                <button className="btn-close" onClick={closeInviteModal}><X size={24} /></button>
              </div>
              {inviteResult ? (
                /* F1-3: success view — surface the invite link with a Copy button */
                <div className="modal-form">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#1E4A35', fontWeight: 600 }}>
                    <CheckCircle2 size={20} />
                    <p style={{ margin: 0 }}>Invitation emailed to {inviteResult.email}</p>
                  </div>
                  <div className="form-group">
                    <label>Invite link</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input type="text" readOnly value={inviteResult.inviteLink}
                        onFocus={(e) => e.target.select()} style={{ flex: 1, fontSize: '0.82rem' }} />
                      <button type="button" className="btn-secondary" style={{ whiteSpace: 'nowrap' }}
                        onClick={() => copyInviteLink(inviteResult.inviteLink, 'modal')}>
                        <Copy size={15} /> {copiedInviteId === 'modal' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: '0.82rem', color: '#666', lineHeight: 1.5 }}>
                      Share this link directly if the email doesn't arrive. It expires in 30 days,
                      and stays available under the club's pending invites.
                    </p>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-primary" onClick={closeInviteModal}>Done</button>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleInviteSubmit} className="modal-form">
                {formError && <div className="error-box"><AlertCircle size={18} /><p>{formError}</p></div>}
                <div className="form-group">
                  <label>Email Address *</label>
                  <input type="email" placeholder="admin@example.com" value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>
                    They'll be set up as this club's <strong>Club Admin</strong> — the first club-level
                    account. Once they're in, they can invite team managers, coaches, players and
                    further admins themselves.
                  </p>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeInviteModal}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={inviteMutation.isPending}>
                    {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
